/**
 * Step 1 Hourly - Mise à disposition
 * Sélection : adresse de départ (avec géoloc) + adresse de fin + durée (slider) + véhicule avec prix
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useBooking } from '../../context/BookingContext';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import { 
  Clock, MapPin, Car, Users, Briefcase, ChevronRight,
  Loader2, AlertCircle, Info, Navigation, MapPinOff
} from 'lucide-react';
import axios from 'axios';
import { pricingAPI } from '../../services/bookingApi';
import config from 'config';

const API_URL = config.API_URL;

// Formatage durée pour affichage
const formatDuration = (hours) => {
  if (hours === Math.floor(hours)) {
    return `${hours}h`;
  }
  const h = Math.floor(hours);
  const m = (hours - h) * 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

// Obtenir la date/heure actuelle formatée pour datetime-local
const getCurrentDateTime = () => {
  const now = new Date();
  // Ajouter 1 heure pour avoir un délai minimum
  now.setHours(now.getHours() + 1);
  // Format: YYYY-MM-DDTHH:MM
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(Math.ceil(now.getMinutes() / 15) * 15 % 60).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const Step1Hourly = () => {
  const { bookingData, updateBookingData, nextStep } = useBooking();
  
  // État local - Date/heure par défaut = maintenant + 1h
  const [pickup, setPickup] = useState(bookingData.pickup);
  const [dropoff, setDropoff] = useState(bookingData.dropoff);
  const [pickupDatetime, setPickupDatetime] = useState(bookingData.pickup_datetime || getCurrentDateTime());
  const [duration, setDuration] = useState(bookingData.hours_booked || 2);
  const [selectedVehicle, setSelectedVehicle] = useState(bookingData.vehicle_category || null);
  
  // Géolocalisation
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);
  
  // Config tarif horaire
  const [hourlyConfig, setHourlyConfig] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [prices, setPrices] = useState({});
  const [error, setError] = useState(null);

  // Géolocalisation automatique au chargement
  useEffect(() => {
    if (!pickup && navigator.geolocation) {
      handleGeolocation();
    }
  }, []);

  // Géolocalisation de l'utilisateur
  const handleGeolocation = async () => {
    if (!navigator.geolocation) {
      setGeoError("Géolocalisation non supportée");
      return;
    }

    setGeoLoading(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Appeler l'API de reverse geocoding
          const response = await axios.get(
            `${API_URL}/api/pricing/reverse-geocode?lat=${latitude}&lng=${longitude}`
          );
          
          if (response.data.address) {
            setPickup({
              address: response.data.address,
              latitude: latitude,
              longitude: longitude,
              place_id: response.data.place_id || null
            });
          }
        } catch (err) {
          console.error('Reverse geocoding error:', err);
          setGeoError("Impossible de déterminer l'adresse");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        console.error('Geolocation error:', err);
        setGeoError(err.code === 1 ? "Géolocalisation refusée" : "Erreur de localisation");
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Charger la config tarif horaire et véhicules
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        
        const hourlyRes = await axios.get(`${API_URL}/api/tariffs/hourly/public`);
        
        const tariff = hourlyRes.data.tariff || hourlyRes.data;
        setHourlyConfig(tariff);
        
        const vehicleList = (tariff?.rates || []).map(rate => ({
          id: rate.vehicle_type,
          name: rate.vehicle_name || rate.vehicle_type,
          max_passengers: rate.max_passengers || 4,
          max_luggage: rate.max_luggage || 3
        }));
        setVehicles(vehicleList);
        
        if (tariff.min_duration) {
          setDuration(prev => prev < tariff.min_duration ? tariff.min_duration : prev);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading hourly config:', err);
        setError('Impossible de charger la configuration. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, []);

  // État pour la distance estimée
  const [estimatedDistance, setEstimatedDistance] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);

  // Calculer la distance entre pickup et dropoff
  useEffect(() => {
    const calculateDistance = async () => {
      if (!pickup?.latitude || !dropoff?.latitude) {
        setEstimatedDistance(null);
        return;
      }
      
      // Si pickup et dropoff sont identiques, distance = 0
      if (pickup.latitude === dropoff.latitude && pickup.longitude === dropoff.longitude) {
        setEstimatedDistance(0);
        return;
      }

      setDistanceLoading(true);
      try {
        // Utiliser l'API Google Directions via notre backend
        const response = await axios.post(`${API_URL}/api/pricing/calculate-distance`, {
          origin_lat: pickup.latitude,
          origin_lng: pickup.longitude,
          dest_lat: dropoff.latitude,
          dest_lng: dropoff.longitude
        });
        
        if (response.data.distance_km) {
          setEstimatedDistance(response.data.distance_km);
        }
      } catch (err) {
        console.error('Distance calculation error:', err);
        // Fallback: calcul à vol d'oiseau × 1.3
        const R = 6371; // Rayon de la Terre en km
        const dLat = (dropoff.latitude - pickup.latitude) * Math.PI / 180;
        const dLon = (dropoff.longitude - pickup.longitude) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(pickup.latitude * Math.PI / 180) * Math.cos(dropoff.latitude * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c * 1.3; // × 1.3 pour approximer la route
        setEstimatedDistance(Math.round(distance * 10) / 10);
      } finally {
        setDistanceLoading(false);
      }
    };

    calculateDistance();
  }, [pickup, dropoff]);

  // Calculer les prix quand la durée ou la distance change
  const calculatePrices = useCallback(async () => {
    if (!hourlyConfig?.rates || hourlyConfig.rates.length === 0) return;
    if (!pickup?.latitude || !pickup?.longitude) return;

    setCalculating(true);

    const newPrices = {};

    try {
      for (const rate of hourlyConfig.rates) {
        if (!rate.price_per_hour) continue;

        try {
          const response = await pricingAPI.calculatePrice({
            pickup_address: pickup.address,
            pickup_lat: pickup.latitude,
            pickup_lng: pickup.longitude,
            dropoff_address: (dropoff || pickup)?.address || pickup.address,
            dropoff_lat: (dropoff || pickup)?.latitude || pickup.latitude,
            dropoff_lng: (dropoff || pickup)?.longitude || pickup.longitude,
            pickup_datetime: pickupDatetime ? new Date(pickupDatetime).toISOString() : new Date().toISOString(),
            vehicle_category: rate.vehicle_type,
            service_type: 'hourly',
            passengers: 1,
            luggage: 0,
            is_round_trip: false,
            hours_booked: duration,
            options: [],
          });

          const details = response.price_details;
          if (!details) continue;

          newPrices[rate.vehicle_type] = {
            total: Math.round((details.total_ttc || 0) * 100) / 100,
            // On laisse l'affichage détaillé (€/h, km inclus) basé sur hourlyConfig
            // Le backend gère désormais les suppléments nuit/week-end dans total_ttc
            surcharges: details.surcharges || [],
            surchargesTotal: details.surcharges_total || 0,
            estimatedDistance,
          };
        } catch (err) {
          console.error('Hourly V2 pricing error for vehicle', rate.vehicle_type, err);
        }
      }

      setPrices(newPrices);
    } finally {
      setCalculating(false);
    }
  }, [pickup, dropoff, pickupDatetime, duration, hourlyConfig, estimatedDistance]);

  useEffect(() => {
    if (hourlyConfig) {
      calculatePrices();
    }
  }, [calculatePrices, hourlyConfig]);

  // Gérer la sélection du véhicule
  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicle(vehicleId);
  };

  // Validation et passage à l'étape suivante
  const handleContinue = () => {
    if (!pickup || !pickupDatetime || !selectedVehicle) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    
    const priceInfo = prices[selectedVehicle];
    const vehicleInfo = vehicles.find(v => v.id === selectedVehicle);
    
    updateBookingData({
      service_type: 'hourly',
      pickup,
      dropoff: dropoff || pickup, // Si pas de fin, utiliser le départ
      pickup_datetime: pickupDatetime,
      hours_booked: duration,
      vehicle_category: selectedVehicle,
      price_estimate: priceInfo?.total || null,
      price_details: priceInfo ? {
        base_price: priceInfo.basePrice || priceInfo.total,
        price_per_hour: priceInfo.perHour,
        duration_hours: duration,
        km_included: priceInfo.kmIncluded,
        extra_km_price: priceInfo.extraKmPrice,
        extra_km: priceInfo.extraKm || 0,
        extra_km_charge: priceInfo.extraKmCharge || 0,
        estimated_distance: priceInfo.estimatedDistance || null,
        pricing_method: 'hourly',
        vehicle_name: vehicleInfo?.name || selectedVehicle,
        surcharges: [],
        options_total: 0,
        total_ht: priceInfo.total / 1.1, // TVA 10%
        total_ttc: priceInfo.total
      } : null,
      is_round_trip: false,
    });
    
    nextStep();
  };

  // Générer les dates/heures min (maintenant)
  const getMinDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
        <span className="ml-3 text-[#E5E5E5]/60">Chargement...</span>
      </div>
    );
  }

  const minDuration = hourlyConfig?.min_duration || 1;
  const maxDuration = hourlyConfig?.max_duration || 12;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="font-['Playfair_Display'] text-xl sm:text-2xl font-bold text-[#E5E5E5] mb-2">
          Mise à disposition
        </h2>
        <p className="text-[#E5E5E5]/60 text-sm">
          Réservez un véhicule avec chauffeur pour la durée de votre choix
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* 1. Adresse de prise en charge avec géolocalisation */}
      <div className="space-y-2">
        <label className="block text-[#E5E5E5] font-medium text-sm">
          <MapPin className="w-4 h-4 inline mr-2 text-[#C9A24D]" />
          Adresse de prise en charge *
        </label>
        <div className="relative">
          <GooglePlacesAutocomplete
            value={pickup}
            onChange={setPickup}
            placeholder="Entrez l'adresse de départ"
            testId="hourly-pickup-input"
          />
          <button
            type="button"
            onClick={handleGeolocation}
            disabled={geoLoading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-lg transition-colors"
            title="Utiliser ma position"
          >
            {geoLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5" />
            )}
          </button>
        </div>
        {geoError && (
          <p className="text-yellow-400 text-xs flex items-center gap-1">
            <MapPinOff className="w-3 h-3" /> {geoError}
          </p>
        )}
        {pickup && (
          <p className="text-green-400 text-xs">✓ {pickup.address}</p>
        )}
      </div>

      {/* 2. Adresse de fin de service */}
      <div className="space-y-2">
        <label className="block text-[#E5E5E5] font-medium text-sm">
          <MapPin className="w-4 h-4 inline mr-2 text-[#E5E5E5]/50" />
          Adresse de fin de service <span className="text-[#E5E5E5]/40">(optionnel)</span>
        </label>
        <GooglePlacesAutocomplete
          value={dropoff}
          onChange={setDropoff}
          placeholder="Même adresse si non renseignée"
          testId="hourly-dropoff-input"
        />
        {dropoff && (
          <p className="text-[#E5E5E5]/60 text-xs">→ {dropoff.address}</p>
        )}
      </div>

      {/* 3. Date et heure */}
      <div className="space-y-2">
        <label className="block text-[#E5E5E5] font-medium text-sm">
          <Clock className="w-4 h-4 inline mr-2 text-[#C9A24D]" />
          Date et heure de début *
        </label>
        <input
          type="datetime-local"
          value={pickupDatetime}
          onChange={(e) => setPickupDatetime(e.target.value)}
          min={getMinDateTime()}
          className="w-full bg-[#0B0B0B] border border-[#C9A24D]/30 rounded-lg px-4 py-3 text-[#E5E5E5] focus:outline-none focus:border-[#C9A24D]"
          data-testid="hourly-datetime-input"
        />
      </div>

      {/* 4. Durée avec slider */}
      <div className="space-y-4 bg-[#0B0B0B] rounded-lg p-4 border border-[#C9A24D]/20">
        <div className="flex items-center justify-between">
          <label className="text-[#E5E5E5] font-medium text-sm">
            <Clock className="w-4 h-4 inline mr-2 text-[#C9A24D]" />
            Durée de la prestation
          </label>
          <span className="text-[#C9A24D] font-bold text-xl">
            {formatDuration(duration)}
          </span>
        </div>
        
        <div className="space-y-2">
          <input
            type="range"
            min={minDuration}
            max={maxDuration}
            step="0.5"
            value={duration}
            onChange={(e) => setDuration(parseFloat(e.target.value))}
            className="w-full h-2 bg-[#1F1F1F] rounded-lg appearance-none cursor-pointer accent-[#C9A24D]"
            data-testid="hourly-duration-slider"
          />
          <div className="flex justify-between text-xs text-[#E5E5E5]/40">
            <span>{formatDuration(minDuration)} min</span>
            <span>{formatDuration(maxDuration)} max</span>
          </div>
        </div>

        {/* Input numérique pour précision */}
        <div className="flex items-center gap-3 pt-2">
          <span className="text-[#E5E5E5]/60 text-sm">Ou saisissez :</span>
          <input
            type="number"
            min={minDuration}
            max={maxDuration}
            step="0.5"
            value={duration}
            onChange={(e) => {
              let val = parseFloat(e.target.value);
              if (isNaN(val)) val = minDuration;
              if (val < minDuration) val = minDuration;
              if (val > maxDuration) val = maxDuration;
              setDuration(val);
            }}
            className="w-20 bg-[#1F1F1F] border border-[#C9A24D]/30 rounded px-3 py-1 text-center text-[#E5E5E5] focus:outline-none focus:border-[#C9A24D]"
            data-testid="hourly-duration-input"
          />
          <span className="text-[#E5E5E5]/60 text-sm">heures</span>
        </div>
      </div>

      {/* 5. Sélection véhicule avec prix */}
      <div className="space-y-3">
        <label className="block text-[#E5E5E5] font-medium text-sm mb-3">
          <Car className="w-4 h-4 inline mr-2 text-[#C9A24D]" />
          Choisissez votre véhicule *
        </label>
        
        {calculating ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 text-[#C9A24D] animate-spin" />
            <span className="ml-2 text-[#E5E5E5]/60 text-sm">Calcul des prix...</span>
          </div>
        ) : (
          <div className="space-y-3">
            {vehicles.map((vehicle) => {
              const priceInfo = prices[vehicle.id];
              const isSelected = selectedVehicle === vehicle.id;
              const hasPrice = priceInfo?.total;
              
              return (
                <button
                  key={vehicle.id}
                  onClick={() => hasPrice && handleVehicleSelect(vehicle.id)}
                  disabled={!hasPrice}
                  data-testid={`hourly-vehicle-${vehicle.id}`}
                  className={`
                    w-full p-4 rounded-lg border-2 transition-all text-left
                    ${isSelected 
                      ? 'border-[#C9A24D] bg-[#C9A24D]/10' 
                      : hasPrice
                        ? 'border-[#E5E5E5]/10 hover:border-[#C9A24D]/50 bg-[#0B0B0B]'
                        : 'border-[#E5E5E5]/5 bg-[#0B0B0B]/50 opacity-50 cursor-not-allowed'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Radio indicator */}
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${isSelected ? 'border-[#C9A24D] bg-[#C9A24D]' : 'border-[#E5E5E5]/30'}
                      `}>
                        {isSelected && <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" />}
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#E5E5E5]">
                          {vehicle.name}
                        </h4>
                        <div className="flex items-center gap-3 text-[#E5E5E5]/50 text-sm mt-1">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {vehicle.max_passengers}
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {vehicle.max_luggage}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      {hasPrice ? (
                        <>
                          <p className="text-[#C9A24D] font-bold text-xl">
                            {priceInfo.total.toFixed(0)} €
                          </p>
                          <p className="text-[#E5E5E5]/40 text-xs">
                            {priceInfo.perHour}€/h × {formatDuration(duration)}
                            {priceInfo.extraKmCharge > 0 && ` + ${priceInfo.extraKmCharge}€`}
                          </p>
                          {priceInfo.kmIncluded && (
                            <p className="text-[#E5E5E5]/30 text-xs">
                              {Math.round(priceInfo.kmIncluded)} km inclus
                            </p>
                          )}
                          {priceInfo.extraKm > 0 && (
                            <p className="text-orange-400 text-xs">
                              +{priceInfo.extraKm} km supp. ({priceInfo.extraKmPrice}€/km)
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-[#E5E5E5]/40 text-sm">Non disponible</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Info distance estimée */}
      {estimatedDistance !== null && estimatedDistance > 0 && (
        <div className="bg-[#1F1F1F] border border-[#E5E5E5]/10 rounded-lg p-4 flex gap-3">
          <MapPin className="w-5 h-5 text-[#E5E5E5]/50 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-[#E5E5E5]/70">
              Distance estimée : <span className="text-[#E5E5E5] font-medium">{Math.round(estimatedDistance)} km</span>
            </p>
            {(() => {
              const rate = hourlyConfig?.rates?.find(r => r.vehicle_type === selectedVehicle);
              const kmIncluded = rate?.km_included_per_hour ? rate.km_included_per_hour * duration : 0;
              if (estimatedDistance > kmIncluded) {
                return (
                  <p className="text-orange-400 text-xs mt-1">
                    ⚠️ La distance dépasse les {Math.round(kmIncluded)} km inclus. 
                    Des frais supplémentaires s'appliquent.
                  </p>
                );
              }
              return (
                <p className="text-green-400 text-xs mt-1">
                  ✓ Distance couverte par les km inclus
                </p>
              );
            })()}
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg p-4 flex gap-3">
        <Info className="w-5 h-5 text-[#C9A24D] flex-shrink-0" />
        <div className="text-sm text-[#E5E5E5]/70">
          <p className="font-medium text-[#E5E5E5] mb-1">Flexibilité totale</p>
          <p>Le chauffeur reste à votre disposition pendant toute la durée réservée. 
          Vous pouvez faire autant d'arrêts que nécessaire.</p>
          {hourlyConfig?.rates?.[0]?.extra_km_price && (
            <p className="text-[#E5E5E5]/50 text-xs mt-2">
              Km supplémentaires facturés à {hourlyConfig.rates[0].extra_km_price}€/km au-delà des km inclus.
            </p>
          )}
        </div>
      </div>

      {/* Bouton continuer */}
      <button
        onClick={handleContinue}
        disabled={!pickup || !pickupDatetime || !selectedVehicle}
        data-testid="hourly-continue-btn"
        className={`
          w-full py-4 rounded-lg font-semibold text-lg transition-all
          flex items-center justify-center gap-2
          ${pickup && pickupDatetime && selectedVehicle
            ? 'bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]'
            : 'bg-[#E5E5E5]/10 text-[#E5E5E5]/40 cursor-not-allowed'
          }
        `}
      >
        Continuer
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Step1Hourly;
